import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { List, Collapse } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { flag, useFeatureFlag } from 'hooks/useFeatureFlag';
import { SESSION_TYPE } from 'types/chat';
import { getVerifiedTemplateFullInfo } from 'services/getVerifiedTemplateFullInfo';
import { TemplateInfo } from 'services/types';
import ChatItem from '../ChatItem';
import ChatsContext from '../../contexts/chats/context';
import { ICollapseView } from '../../contexts/menuChats/types';
import MenuChatsContext from '../../contexts/menuChats/context';
import ConversationContext from '../../contexts/conversation/context';
import { IRoom } from '../../types/room';
import { PartialDivider } from './style';
import { ITemplate } from './types';

export const CollapseView = ({ item, type, expanded, timeout }: ICollapseView) => {
  const { goToNewChat, serviceSelected, notificationSelected } = useContext(ChatsContext);
  const { openStartNewConversation, handleStartNewConversation } = useContext(ConversationContext);
  const isFIFO = useFeatureFlag(flag.FIFO_INQUIRIES);
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const shouldExpand = expanded !== undefined ? expanded : item.expanded;
  const navigate = useNavigate();

  const {
    setOpenContactManager,
    openContactManager,
    setOpenChatsManager,
    openChatsManager,
    setOpenServiceDashboard,
    openServiceDashboard,
  } = useContext(MenuChatsContext);
  const handleStartService = (service: IRoom) => {
    const serviceMap: [boolean, Dispatch<SetStateAction<boolean>>][] = [
      [openContactManager, setOpenContactManager],
      [openChatsManager, setOpenChatsManager],
      [openServiceDashboard, setOpenServiceDashboard],
    ];
    serviceMap.forEach(([value, setter]) => {
      if (value) {
        setter(() => false);
      }
    });

    goToNewChat(service);
  };

  const convertTemplateToRoom = (template: TemplateInfo, currentIndex: number) => ({
    _id: item?.templates?.[currentIndex].id || '',
    lastMessage: {
      _id: template.ID,
      msg: template.BodyText,
      t: template.Name,
      ts: item?.templates?.[currentIndex].created_at || '',
      unread: false,
      u: {
        _id: template.ID,
        username: item?.templates?.[currentIndex].contact.name || '',
        name: item?.templates?.[currentIndex].contact.name || '',
      },
    },
    fname: item?.templates?.[currentIndex].contact.name || '',
    tags: [],
    unreadChat: false,
    unreadMessages: 0,
    hasDiscussion: false,
  });

  const goToNotification = (service: IRoom) => {
    const drawerStates: [boolean, Dispatch<SetStateAction<boolean>> | ((open: boolean) => void)][] = [
      [openContactManager, setOpenContactManager],
      [openChatsManager, setOpenChatsManager],
      [openServiceDashboard, setOpenServiceDashboard],
      [openStartNewConversation, handleStartNewConversation],
    ];
    drawerStates.forEach(([value, setter]) => {
      if (value) {
        setter(false);
      }
    });

    navigate(`/notification/${service._id}`);
  };

  useEffect(() => {
    if (item.id === SESSION_TYPE.STARTED_BY_YOU && item.templates?.length) {
      const templateInfoPromises = item.templates?.map((template) => getVerifiedTemplateFullInfo(template.template.id));

      Promise.all(templateInfoPromises)
        .then((templatesPromises) => {
          const templatesInfo = templatesPromises.map((template, index) => convertTemplateToRoom(template.data, index));
          setTemplates(templatesInfo);
        })
        .catch((error) => {
          console.error('Error fetching template info:', error);
        });
    }
  }, [item.templates, item.id]);

  const itemRoomsParsed = useMemo(() => {
    const filteredRooms = item.rooms?.filter(({ lastMessage }: IRoom) => !!lastMessage?.ts) ?? [];
    if (isFIFO) {
      return filteredRooms;
    }

    return filteredRooms.sort(
      ({ lastMessage: { ts: aLast } }: IRoom, { lastMessage: { ts: bLast } }: IRoom) =>
        new Date(bLast).getTime() - new Date(aLast).getTime()
    );
  }, [item.rooms, isFIFO]);

  const isStartedByYou = item.id === SESSION_TYPE.STARTED_BY_YOU;
  const isStartedByYouAndHasTemplates = isStartedByYou && templates.length > 0;

  const data = isStartedByYouAndHasTemplates ? templates : itemRoomsParsed;

  return (
    <Collapse in={shouldExpand} timeout={timeout}>
      <List component="div" disablePadding sx={{ display: 'flex', flexDirection: 'column' }}>
        {data.map((room: IRoom & { hasDiscussion: boolean }) => {
          const { _id, lastMessage, fname, tags, unreadChat, unreadMessages, hasDiscussion } = room;
          const isCurrentChat = serviceSelected?._id === _id || notificationSelected?._id === _id;
          return (
            <React.Fragment key={_id}>
              <ChatItem
                iscurrentchat={isCurrentChat}
                key={_id}
                id={_id}
                onClick={isStartedByYouAndHasTemplates ? () => goToNotification(room) : () => handleStartService(room)}
                message={lastMessage}
                name={fname}
                tags={tags}
                hasError={false}
                sessionType={item.id}
                unreadChat={!!unreadChat}
                unreadMessages={unreadMessages || 0}
                room={room}
                tabType={type}
                hasDiscussion={hasDiscussion}
              />

              <PartialDivider component="li" />
            </React.Fragment>
          );
        })}
      </List>
    </Collapse>
  );
};
